import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd from '@/components/base/JsonLd';
import SeriesCard from '@/components/feature/SeriesCard';
import { seriesList, categoryMap } from '@/mocks/series';
import { statusInfo, formatHeat } from '@/utils/series';

const followKey = (id: string) => `chigua-follow-${id}`;

export default function Story() {
  const { id = '' } = useParams();
  const series = seriesList.find((s) => s.id === id);

  const [following, setFollowing] = useState<boolean>(() => {
    try {
      return localStorage.getItem(followKey(id)) === '1';
    } catch {
      return false;
    }
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setFollowing(localStorage.getItem(followKey(id)) === '1');
    setCopied(false);
  }, [id]);

  if (!series) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-2xl flex-col items-center justify-center px-4 py-32 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground-950">这一部「瓜剧」暂时找不到</h1>
          <p className="mt-3 text-sm text-foreground-500">它可能还没有被收录，或链接有误。回追更大厅看看其他系列吧。</p>
          <Link
            to="/chasing"
            className="mt-7 inline-flex h-11 items-center whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line mr-1.5"></i>返回追更大厅
          </Link>
        </div>
      </Layout>
    );
  }

  const st = statusInfo[series.status];
  const cat = categoryMap.find((c) => c.key === series.categoryKey);
  const last = series.episodes[series.episodes.length - 1];
  const related = seriesList
    .filter((s) => s.id !== series.id)
    .sort((a, b) => {
      const score = (x: typeof a) => (x.categoryKey === series.categoryKey ? 1 : 0) * 100 + x.heat;
      return score(b) - score(a);
    })
    .slice(0, 3);

  const toggleFollow = () => {
    const next = !following;
    setFollowing(next);
    try {
      localStorage.setItem(followKey(id), next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: '追更大厅', item: '/chasing' },
      { '@type': 'ListItem', position: 3, name: series.title },
    ],
  };
  const itemSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${series.title} · 剧情时间线`,
    itemListElement: series.episodes.map((e, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `${e.ep} ${e.title}`,
    })),
  };

  return (
    <Layout>
      <JsonLd data={[breadcrumbSchema, itemSchema]} />

      {/* 头部横幅 */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-400 pb-14 pt-24 text-background-50 md:pb-20 md:pt-28">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <nav aria-label="面包屑" className="text-xs text-background-100/80">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-background-50">首页</Link></li>
                <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/chasing" className="hover:text-background-50">追更大厅</Link></li>
                <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
                <li className="font-medium text-background-50">{series.categoryName}</li>
              </ol>
            </nav>

            <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
              <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${st.badge}`}>
                {st.text}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50/15 px-3 py-1 text-xs">
                <i className={`${cat?.icon || 'ri-folder-line'} mr-1`}></i>
                {series.categoryName}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50/15 px-3 py-1 text-xs">
                <i className="ri-flashlight-line"></i>
                热度 {formatHeat(series.heat)}
              </span>
            </div>

            <h1 className="mt-5 font-heading text-3xl leading-tight text-background-50 md:text-5xl">{series.title}</h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-background-100/95 md:text-base md:leading-8">{series.summary}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {series.tags.map((t) => (
                <span key={t} className="whitespace-nowrap rounded-full border border-background-50/25 px-3 py-1 text-xs text-background-50/90">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={toggleFollow}
                className={`flex h-11 items-center justify-center whitespace-nowrap rounded-full px-6 text-sm font-bold transition-all ${
                  following
                    ? 'bg-background-50 text-primary-700'
                    : 'bg-secondary-400 text-foreground-950 hover:bg-secondary-300'
                }`}
              >
                <i className={`${following ? 'ri-heart-fill' : 'ri-heart-add-line'} mr-1.5 text-base`}></i>
                {following ? '已在追更' : '加入追更'}
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="flex h-11 items-center justify-center whitespace-nowrap rounded-full border border-background-50/35 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-link'} mr-1.5 text-base`}></i>
                {copied ? '链接已复制' : '复制追更链接'}
              </button>
              <span className="text-sm text-background-100/90">
                {following ? '已开启，剧情更新会第一时间关注' : `最新进展：${last?.ep} · ${last?.date}`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 主体：时间线 + 侧栏 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 lg:grid-cols-12">
          {/* 时间线 */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <i className="ri-time-line text-primary-500"></i>
                剧情时间线 · {series.latestEp}更新
              </h2>
              <span className="text-sm text-foreground-500">共 {series.episodes.length} 条关键进展</span>
            </div>

            <ol className="relative mt-8 space-y-5 border-l border-background-200 pl-6">
              {series.episodes.map((e, i) => {
                const isNewest = i === series.episodes.length - 1;
                return (
                  <li key={e.ep + e.date} className="relative">
                    <span
                      className={`absolute -left-[1.85rem] top-5 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center rounded-full ring-4 ring-background-50 ${
                        isNewest ? 'bg-accent-500' : 'bg-primary-400'
                      }`}
                    ></span>
                    <article className="overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-0.5 hover:border-primary-200">
                      <div className="flex flex-wrap items-center gap-2 border-b border-background-200/70 bg-background-100/50 px-4 py-3 md:px-5">
                        <span className="rounded-md bg-foreground-950 px-2 py-0.5 text-xs font-bold text-background-50">
                          {e.ep}
                        </span>
                        <span className="text-xs font-bold text-foreground-500">
                          <i className="ri-calendar-line mr-1"></i>{e.date}
                        </span>
                        <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-bold text-secondary-900">热度{e.tag}</span>
                        {isNewest && (
                          <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-accent-600">
                            <i className="ri-sparkling-line"></i>最新进展
                          </span>
                        )}
                      </div>
                      <div className="px-4 py-4 md:px-5 md:py-5">
                        <h3 className="text-base font-bold text-foreground-950 md:text-lg">{e.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-foreground-700">{e.summary}</p>
                        <ul className="mt-3 flex flex-col gap-2">
                          {e.points.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-sm text-foreground-600">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ol>

            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-5 py-4 text-sm text-foreground-600">
              <i className="ri-information-line text-accent-600"></i>
              本条系列为虚构娱乐剧情演示，剧情更新与后续进展将随内容持续补充，理性吃瓜。
            </div>
          </div>

          {/* 侧栏 */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                <div className="relative h-28 w-full overflow-hidden bg-background-200">
                  <img src={series.cover} alt={`${series.title} 头图`} className="h-full w-full object-cover object-top" />
                  <span className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent"></span>
                  <span className={`absolute left-3 top-3 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold text-background-50 ${st.badge}`}>
                    {st.text}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-foreground-950">系列档案</h3>
                  <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-xs text-foreground-400">所属类型</dt>
                      <dd className="mt-1 font-medium text-foreground-800">{series.categoryName}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-400">最新更新</dt>
                      <dd className="mt-1 font-medium text-foreground-800">{last?.date} · {last?.ep}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-400">更新状态</dt>
                      <dd className="mt-1 font-medium text-foreground-800">{series.totalEps}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-400">关注热度</dt>
                      <dd className="mt-1 font-medium text-primary-600">{formatHeat(series.heat)}</dd>
                    </div>
                  </dl>

                  <div className="mt-5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">追更进度</span>
                      <span className="font-bold text-primary-600">{series.episodes.length}/{series.episodes.length} 已读</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"></div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={toggleFollow}
                    className={`mt-5 flex h-11 w-full items-center justify-center whitespace-nowrap rounded-full text-sm font-bold transition-colors ${
                      following ? 'bg-background-100 text-primary-600 hover:bg-secondary-100' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    }`}
                  >
                    <i className={`${following ? 'ri-heart-fill' : 'ri-heart-add-line'} mr-1.5 text-base`}></i>
                    {following ? '正在追更这部' : '追这部连续剧'}
                  </button>
                  <Link
                    to={`/chasing?cat=${series.categoryKey}`}
                    className="mt-2.5 flex h-11 w-full items-center justify-center whitespace-nowrap rounded-full border border-background-200 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    看同类型更多
                  </Link>
                </div>
              </div>

              <div className="mt-5 rounded-2xl bg-secondary-100/70 p-5">
                <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                  <i className="ri-bell-line text-primary-500"></i>
                  追更小贴士
                </h3>
                <p className="mt-2 text-sm leading-6 text-foreground-700">
                  点击「加入追更」即可把这部连续剧放在心头，后续剧情更新时别错过关键反转。翻到最新一集，等更新也很美好。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-2xl">
              <i className="ri-magic-line text-primary-500"></i>
              这部完结了，还有这些值得追
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((s) => (
                <SeriesCard key={s.id} series={s} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}